"use client";

import { c as _c } from "react/compiler-runtime";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FocusTrap } from "focus-trap-react";
import { useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useClickAway, useKeyPressEvent } from "react-use";
import CreateWaveGroupInlinePanel from "@/components/waves/create-wave/groups/CreateWaveGroupInlinePanel";
import { buildInlineGroupName } from "@/components/waves/create-wave/groups/createWaveInlineGroupBuilder";
import { CREATE_WAVE_NONE_GROUP_LABELS, CREATE_WAVE_SELECT_GROUP_LABELS } from "@/helpers/waves/waves.constants";
import type { ApiCreateGroup } from "@/generated/models/ApiCreateGroup";
import type { ApiGroup } from "@/generated/models/ApiGroup";
import { ApiGroupFilterDirection } from "@/generated/models/ApiGroupFilterDirection";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { ApiGroupTdhInclusionStrategy } from "@/generated/models/ApiGroupTdhInclusionStrategy";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import type { ApiWave } from "@/generated/models/ApiWave";
import { CreateWaveGroupConfigType } from "@/types/waves.types";
import { WaveGroupType } from "../WaveGroup.types";
import { ApiProfileClassification } from "@/generated/models/ApiProfileClassification";
const WAVE_GROUP_TO_CREATE_GROUP_TYPE = {
  [WaveGroupType.VIEW]: CreateWaveGroupConfigType.CAN_VIEW,
  [WaveGroupType.DROP]: CreateWaveGroupConfigType.CAN_DROP,
  [WaveGroupType.VOTE]: CreateWaveGroupConfigType.CAN_VOTE,
  [WaveGroupType.CHAT]: CreateWaveGroupConfigType.CAN_CHAT,
  [WaveGroupType.ADMIN]: CreateWaveGroupConfigType.ADMIN
} satisfies Record<WaveGroupType, CreateWaveGroupConfigType>;
const createEmptyGroupDescription = (): ApiGroupFull["group"] => ({
  tdh: {
    min: null,
    max: null,
    inclusion_strategy: ApiGroupTdhInclusionStrategy.Both
  },
  rep: {
    min: null,
    max: null,
    direction: ApiGroupFilterDirection.Received,
    user_identity: null,
    category: null
  },
  cic: {
    min: null,
    max: null,
    direction: ApiGroupFilterDirection.Received,
    user_identity: null
  },
  level: {
    min: null,
    max: null
  },
  owns_nfts: [],
  identity_group_id: null,
  identity_group_identities_count: 0,
  excluded_identity_group_id: null,
  excluded_identity_group_identities_count: 0,
  is_beneficiary_of_grant_id: null,
  is_beneficiary_of_grant: null
});
const createUnknownGroupAuthor = (): ApiProfileMin => ({
  id: "unknown",
  handle: null,
  pfp: null,
  banner1_color: null,
  banner2_color: null,
  cic: 0,
  rep: 0,
  tdh: 0,
  tdh_rate: 0,
  xtdh: 0,
  xtdh_rate: 0,
  level: 0,
  primary_address: "",
  subscribed_actions: [],
  archived: false,
  active_main_stage_submission_ids: [],
  winner_main_stage_drop_ids: [],
  artist_of_prevote_cards: [],
  profile_wave_id: null,
  is_wave_creator: false,
  classification: ApiProfileClassification.Pseudonym,
  sub_classification: null
});
const getSelectedGroup = (group: ApiGroup | null): ApiGroupFull | null => {
  if (!group?.id || !group.name) {
    return null;
  }
  return {
    id: group.id,
    name: group.name,
    group: createEmptyGroupDescription(),
    created_at: group.created_at ?? 0,
    created_by: group.author ?? createUnknownGroupAuthor(),
    visible: !group.is_hidden,
    is_private: false,
    is_direct_message: group.is_direct_message ?? false
  };
};
export default function WaveGroupChangeDialog(t0) {
  const $ = _c(36);
  const {
    wave,
    type,
    currentGroup,
    onClose,
    onGroupChange,
    onCreateGroup
  } = t0;
  const modalRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  let t1;
  if ($[0] !== currentGroup) {
    t1 = getSelectedGroup(currentGroup);
    $[0] = currentGroup;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedGroup = t1;
  const groupConfigType = WAVE_GROUP_TO_CREATE_GROUP_TYPE[type];
  const groupLabel = CREATE_WAVE_SELECT_GROUP_LABELS[wave.wave.type][groupConfigType];
  const defaultLabel = CREATE_WAVE_NONE_GROUP_LABELS[groupConfigType];
  let t2;
  if ($[2] !== groupLabel || $[3] !== wave.name) {
    t2 = buildInlineGroupName({
      waveName: wave.name,
      groupLabel
    });
    $[2] = groupLabel;
    $[3] = wave.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const suggestedName = t2;
  const title = selectedGroup ? "Change group" : "Add group";
  const description = selectedGroup ? "Create a new group or choose a different existing group." : "Create a new group or choose an existing group.";
  useClickAway(modalRef, onClose);
  let t3;
  if ($[5] !== onClose) {
    t3 = event => {
      if (event.defaultPrevented) {
        return;
      }
      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement && modalRef.current?.contains(activeElement) && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.tagName === "SELECT" || activeElement.isContentEditable || activeElement.getAttribute("role") === "combobox")) {
        return;
      }
      onClose();
    };
    $[5] = onClose;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useKeyPressEvent("Escape", t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      allowOutsideClick: true,
      fallbackFocus: () => modalRef.current ?? document.body
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="tw-fixed tw-inset-0 tw-bg-gray-500 tw-bg-opacity-75" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== title || $[10] !== titleId) {
    t6 = <p id={titleId} className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50">{title}</p>;
    $[9] = title;
    $[10] = titleId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== description || $[13] !== descriptionId) {
    t7 = <p id={descriptionId} className="tw-mb-0 tw-text-sm tw-text-iron-400">{description}</p>;
    $[12] = description;
    $[13] = descriptionId;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <div className="tw-flex tw-flex-col tw-gap-y-2">{t6}{t7}</div>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t10;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="tw-sr-only">Close</span>;
    t10 = <FontAwesomeIcon icon={faXmark} className="tw-h-5 tw-w-5" />;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== onClose) {
    t11 = <button onClick={onClose} type="button" aria-label="Close dialog" className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-p-2 tw-text-iron-400 tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-50">{t9}{t10}</button>;
    $[20] = onClose;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t8) {
    t12 = <div className="tw-flex tw-items-start tw-justify-between tw-gap-x-4">{t8}{t11}</div>;
    $[22] = t11;
    $[23] = t8;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== defaultLabel || $[26] !== onCreateGroup || $[27] !== onGroupChange || $[28] !== selectedGroup || $[29] !== suggestedName) {
    t13 = <div className="tw-mt-6"><CreateWaveGroupInlinePanel suggestedName={suggestedName} defaultLabel={defaultLabel} selectedGroup={selectedGroup} allowGroupClear={false} onChange={onGroupChange} onCreateGroup={onCreateGroup} /></div>;
    $[25] = defaultLabel;
    $[26] = onCreateGroup;
    $[27] = onGroupChange;
    $[28] = selectedGroup;
    $[29] = suggestedName;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== descriptionId || $[32] !== t12 || $[33] !== t13 || $[34] !== titleId) {
    t14 = createPortal(<FocusTrap focusTrapOptions={t4}><div className="tw-relative tw-z-50 tw-cursor-default">{t5}<div className="tw-fixed tw-inset-0 tw-z-10 tw-overflow-y-auto"><div className="tw-flex tw-min-h-full tw-items-end tw-justify-center tw-p-4 tw-text-center sm:tw-items-center sm:tw-p-0"><dialog ref={modalRef} aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} tabIndex={-1} open={true} className="tw-relative tw-w-full tw-transform tw-rounded-xl tw-bg-iron-950 tw-p-6 tw-text-left tw-shadow-xl tw-transition-all tw-duration-500 sm:tw-w-full sm:tw-max-w-2xl">{t12}{t13}</dialog></div></div></div></FocusTrap>, document.body);
    $[31] = descriptionId;
    $[32] = t12;
    $[33] = t13;
    $[34] = titleId;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}